import React, { useState, useEffect, ChangeEvent } from 'react';
import styled from 'styled-components';
import { Tag, Save, Plus, Pencil, Trash2 } from 'lucide-react';
import { CategoriaGusto } from '../types';
import apiService from '../services/api';
import GelatoDataGrid from '../components/DataGrid/GelatoDataGrid';
import { ColDef } from 'ag-grid-community';
import {
  PageContainer,
  PageHeader,
  Button,
  Card,
  CardBody,
  Alert,
  LoadingState,
  Modal,
  FormGroup,
  FormActions,
  Label,
  Input,
  Select,
  useConfirm,
} from '../components/ui';

const ColorInput = styled.input`
  width: 100%;
  height: 48px;
  padding: var(--space-xs);
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius-md);
  background: var(--input-bg);
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-500);
    box-shadow: 0 0 0 3px rgba(147, 153, 1, 0.15);
  }
`;

const GridActionsCell = styled.div`
  display: flex;
  gap: var(--space-sm);
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const GridIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: var(--space-xs);
  border-radius: var(--border-radius-sm);
  color: var(--gray-600);
  cursor: pointer;
  transition: background-color var(--transition-fast), color var(--transition-fast);

  svg {
    pointer-events: none;
  }

  &.btn-edit:hover {
    background: var(--primary-50);
    color: var(--primary-700);
  }

  &.btn-delete:hover {
    background: var(--error-soft);
    color: var(--error);
  }
`;

const columnDefs: ColDef[] = [
  {
    headerName: 'Nome',
    field: 'nome',
    flex: 1,
    minWidth: 150,
    sortable: true,
    filter: true
  },
  {
    headerName: 'Descrizione',
    field: 'descrizione',
    flex: 1.5,
    minWidth: 200,
    sortable: true,
    filter: true,
    cellRenderer: (params: any) => params.value || 'N/A'
  },
  {
    headerName: 'Colore',
    field: 'colore',
    width: 120,
    sortable: true,
    cellRenderer: (params: any) => {
      const colorDiv = React.createElement('div', {
        style: {
          width: '20px',
          height: '20px',
          backgroundColor: params.value,
          borderRadius: '50%',
          border: '1px solid var(--gray-200)'
        }
      });
      const textSpan = React.createElement('span', {}, params.value);

      return React.createElement('div', {
        style: { display: 'flex', alignItems: 'center', gap: '8px' }
      }, [colorDiv, textSpan]);
    }
  },
  {
    headerName: 'Ordine',
    field: 'ordine',
    width: 100,
    sortable: true,
    filter: 'agNumberColumnFilter'
  },
  {
    headerName: 'Stato',
    field: 'attivo',
    width: 100,
    sortable: true,
    cellRenderer: (params: any) => {
      return React.createElement('span', {
        className: params.value ? 'status-badge active' : 'status-badge inactive'
      }, params.value ? 'Attivo' : 'Inattivo');
    }
  },
  {
    headerName: 'Azioni',
    field: 'actions',
    width: 120,
    sortable: false,
    filter: false,
    cellRenderer: (params: any) => (
      <GridActionsCell className="grid-actions-cell" data-row-id={params.data.id}>
        <GridIconButton
          className="btn-edit"
          data-action="edit"
          data-id={params.data.id}
          title="Modifica"
          aria-label="Modifica"
        >
          <Pencil size={16} />
        </GridIconButton>
        <GridIconButton
          className="btn-delete"
          data-action="delete"
          data-id={params.data.id}
          title="Elimina"
          aria-label="Elimina"
        >
          <Trash2 size={16} />
        </GridIconButton>
      </GridActionsCell>
    )
  }
];

const ImpostazioniCategorieGustiPage: React.FC = () => {
  const confirm = useConfirm();
  const [categorie, setCategorie] = useState<CategoriaGusto[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState<CategoriaGusto | null>(null);
  const [alert, setAlert] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success'
  });

  // Form state
  const [formData, setFormData] = useState({
    nome: '',
    descrizione: '',
    colore: '#87CEEB',
    ordine: 0,
    attivo: true
  });

  useEffect(() => {
    caricaCategorie();
  }, []);

  const caricaCategorie = async () => {
    try {
      setLoading(true);
      const data = await apiService.getCategorieGusti();
      setCategorie(data);
    } catch (error) {
      console.error('Errore caricamento categorie:', error);
      setAlert({ show: true, message: 'Errore nel caricamento delle categorie', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked :
              type === 'number' ? parseInt(value) || 0 : value
    }));
  };

  const onGridReady = (params: any) => {
    const gridElement = params.api.getGridOption('domLayout') === 'autoHeight'
      ? params.api.gridOptionsWrapper?.gridOptions?.context?.container
      : document.querySelector('.ag-root-wrapper');

    if (gridElement) {
      gridElement.addEventListener('click', (event: any) => {
        const target = event.target.closest('.btn-edit, .btn-delete');
        if (target) {
          const action = target.dataset.action;
          const id = parseInt(target.dataset.id);

          if (action === 'edit') {
            const categoria = categorie.find(c => c.id === id);
            if (categoria) {
              setEditingCategoria(categoria);
              setFormData({
                nome: categoria.nome,
                descrizione: categoria.descrizione || '',
                colore: categoria.colore,
                ordine: categoria.ordine,
                attivo: categoria.attivo
              });
              setDialogOpen(true);
            }
          } else if (action === 'delete') {
            handleDelete(id);
          }
        }
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCategoria) {
        await apiService.aggiornaCategoriaGusto(editingCategoria.id, formData);
        setAlert({ show: true, message: 'Categoria aggiornata con successo', type: 'success' });
      } else {
        await apiService.creaCategoriaGusto(formData);
        setAlert({ show: true, message: 'Categoria creata con successo', type: 'success' });
      }

      setDialogOpen(false);
      caricaCategorie();
    } catch (error) {
      console.error('Errore salvataggio categoria:', error);
      setAlert({ show: true, message: 'Errore nel salvataggio della categoria', type: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({
      title: 'Elimina categoria',
      message: 'Sei sicuro di voler eliminare questa categoria?',
      confirmLabel: 'Elimina',
      variant: 'danger'
    });
    if (!ok) return;

    try {
      await apiService.eliminaCategoriaGusto(id);
      setAlert({ show: true, message: 'Categoria eliminata con successo', type: 'success' });
      caricaCategorie();
    } catch (error) {
      console.error('Errore eliminazione categoria:', error);
      setAlert({ show: true, message: 'Errore nell\'eliminazione della categoria', type: 'error' });
    }
  };

  const openAddDialog = () => {
    setEditingCategoria(null);
    setFormData({
      nome: '',
      descrizione: '',
      colore: '#87CEEB',
      ordine: 0,
      attivo: true
    });
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingState />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Categorie Gusti"
        icon={<Tag />}
        actions={
          <Button onClick={openAddDialog}>
            <Plus size={16} />
            Aggiungi Categoria
          </Button>
        }
      />

      {alert.show && (
        <Alert $variant={alert.type}>
          {alert.message}
        </Alert>
      )}

      <Card>
        <CardBody>
          <GelatoDataGrid
            rowData={categorie}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
          />
        </CardBody>
      </Card>

      <Modal
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editingCategoria ? 'Modifica Categoria' : 'Nuova Categoria'}
      >
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Nome</Label>
            <Input
              name="nome"
              value={formData.nome}
              onChange={handleInputChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Descrizione</Label>
            <Input
              name="descrizione"
              value={formData.descrizione}
              onChange={handleInputChange}
            />
          </FormGroup>

          <FormGroup>
            <Label>Colore</Label>
            <ColorInput
              type="color"
              name="colore"
              value={formData.colore}
              onChange={handleInputChange}
            />
          </FormGroup>

          <FormGroup>
            <Label>Ordine</Label>
            <Input
              type="number"
              name="ordine"
              value={formData.ordine}
              onChange={handleInputChange}
              min="0"
            />
          </FormGroup>

          <FormGroup>
            <Label>Stato</Label>
            <Select name="attivo" value={formData.attivo.toString()} onChange={handleInputChange}>
              <option value="true">Attivo</option>
              <option value="false">Disattivo</option>
            </Select>
          </FormGroup>

          <FormActions>
            <Button $variant="secondary" type="button" onClick={() => setDialogOpen(false)}>
              Annulla
            </Button>
            <Button type="submit">
              <Save size={16} />
              Salva
            </Button>
          </FormActions>
        </form>
      </Modal>
    </PageContainer>
  );
};

export default ImpostazioniCategorieGustiPage;
